import { useState, useEffect, useRef, useCallback } from "react";
import { Flame, Sparkles } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useNavigate } from "react-router-dom";
import { useAnimatedCounter } from "@/hooks/useAnimatedCounter";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";

const XP_MILESTONES = [100, 250, 500, 1000, 2500, 5000, 10000];
const TIER_THRESHOLDS = [
  { min: 0, label: "Beginner" },
  { min: 500, label: "Intermediate" },
  { min: 2000, label: "Advanced" },
  { min: 5000, label: "Expert" },
  { min: 10000, label: "Master" },
];
const getTier = (xp: number) => {
  for (let i = TIER_THRESHOLDS.length - 1; i >= 0; i--) {
    if (xp >= TIER_THRESHOLDS[i].min) return TIER_THRESHOLDS[i].label;
  }
  return "Beginner";
};

const XPStreakWidget = () => {
  const navigate = useNavigate();
  const [totalXP, setTotalXP] = useState(0);
  const [streak, setStreak] = useState(0);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [userId, setUserId] = useState<string | null>(null);
  const [showGain, setShowGain] = useState(false);
  const [gainAmount, setGainAmount] = useState(0);
  const [pulse, setPulse] = useState(false);
  const previousXP = useRef(0);
  const previousTier = useRef("");
  const { displayValue } = useAnimatedCounter(totalXP, { duration: 600 });

  const fetchData = useCallback(async (uid?: string) => {
    const id = uid || userId;
    if (!id) return;

    const { data } = await supabase
      .from("tinder_user_stats")
      .select("total_xp, current_streak")
      .eq("user_id", id)
      .maybeSingle();

    if (data) {
      const newXP = data.total_xp || 0;
      if (newXP > previousXP.current && previousXP.current > 0) {
        const gain = newXP - previousXP.current;
        setGainAmount(gain);
        setShowGain(true);
        setPulse(true);
        setTimeout(() => setShowGain(false), 2000);
        setTimeout(() => setPulse(false), 800);

        // Check milestone
        for (const milestone of XP_MILESTONES) {
          if (previousXP.current < milestone && newXP >= milestone) {
            toast.success(`🎉 ${milestone.toLocaleString()} XP Milestone!`, { description: "Keep learning and growing!" });
            break;
          }
        }

        // Check tier change
        const oldTier = getTier(previousXP.current);
        const newTier = getTier(newXP);
        if (oldTier !== newTier) {
          toast.success(`🏆 Level Up: ${newTier}!`, { description: `You've graduated from ${oldTier}` });
        }
      }
      previousXP.current = newXP;
      previousTier.current = getTier(newXP);
      localStorage.setItem("finplay_streak_cache", String(data.current_streak || 0));
      setTotalXP(newXP);
      setStreak(data.current_streak || 0);
    }
  }, [userId]);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setIsLoggedIn(!!session);
      if (session?.user) {
        setUserId(session.user.id);
        fetchData(session.user.id);
      }
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setIsLoggedIn(!!session);
      if (session?.user) {
        setUserId(session.user.id);
        fetchData(session.user.id);
      } else {
        setUserId(null);
        setTotalXP(0);
        setStreak(0);
        previousXP.current = 0;
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  // Realtime subscription
  useEffect(() => {
    if (!userId) return;

    const channel = supabase
      .channel(`xp-streak-${userId}`)
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'tinder_user_stats',
        filter: `user_id=eq.${userId}`,
      }, (payload) => {
        if (payload.new && typeof payload.new === 'object') {
          const rec = payload.new as { total_xp?: number; current_streak?: number };
          const newXP = rec.total_xp || 0;
          if (newXP > previousXP.current && previousXP.current > 0) {
            setGainAmount(newXP - previousXP.current);
            setShowGain(true);
            setTimeout(() => setShowGain(false), 2000);
          }
          previousXP.current = newXP;
          setTotalXP(newXP);
          setStreak(rec.current_streak || 0);
        }
      })
      .subscribe();

    return () => { supabase.removeChannel(channel); };
  }, [userId]);

  if (!isLoggedIn) return null;

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          onClick={() => navigate("/rewards-center")}
          className={cn(
            "relative flex items-center gap-2 px-3 py-1.5 rounded-full bg-card border border-border hover:border-primary/30 transition-all",
            pulse && "animate-pulse ring-2 ring-primary/30"
          )}
        >
          {/* Streak */}
          <div className="flex items-center gap-1">
            <Flame className={cn(
              "h-4 w-4 transition-colors",
              streak > 0 ? "text-orange-400" : "text-muted-foreground"
            )} />
            <span className="text-sm font-semibold tabular-nums">{streak}</span>
          </div>

          <div className="w-px h-4 bg-border" />

          {/* XP */}
          <div className="flex items-center gap-1">
            <Sparkles className="h-3.5 w-3.5 text-primary" />
            <span className="text-sm font-semibold tabular-nums">{displayValue.toLocaleString()}</span>
          </div>

          {/* Float-up gain indicator */}
          {showGain && (
            <span className="absolute -top-5 right-0 text-xs font-bold text-primary animate-fade-in pointer-events-none"
              style={{ animation: 'floatUp 1.5s ease-out forwards' }}
            >
              +{gainAmount}
            </span>
          )}
        </button>
      </TooltipTrigger>
      <TooltipContent side="bottom" className="text-center p-2">
        <p className="text-xs text-muted-foreground">
          {streak > 0 ? `${streak}-day streak 🔥` : "Start your streak!"} · {totalXP.toLocaleString()} XP
        </p>
      </TooltipContent>
    </Tooltip>
  );
};

export default XPStreakWidget;
